import 'dayjs/locale/en';
import 'dayjs/locale/he';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {effect, EffectRef, inject, NgZone, OnDestroy, Pipe, PipeTransform, Signal, signal} from '@angular/core';
import {LanguageService} from '@core/language';
import {timerSignal} from '@ddtmm/angular-signal-generators';

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

@Pipe({
	name: 'ElapsedTime',
	standalone: true,
})
export class ElapsedTimePipe implements PipeTransform, OnDestroy {
	private languageService: LanguageService = inject(LanguageService);
	private readonly ngZone: NgZone = inject(NgZone);

	private readonly timeSignal$ = signal<string>('');

	// timer for updating the time every second
	private readonly intervalTimerSignal$ = timerSignal(1000);
	private effectRef$: EffectRef;

	constructor() {
		if (this.languageService.currentLang$() == 'he') dayjs.locale('he');
		else dayjs.locale('en');

		// Start updating the time every second
		this.effectRef$ = effect(() => {
			this.intervalTimerSignal$();
			this.updateTime();
		});
	}

	transform(timestamp: Date | string, timezone: string = dayjs.tz.guess()): Signal<string> {
		if (!timestamp) {
			this.timeSignal$.set('');
			return this.timeSignal$;
		}

		const utcDate = dayjs.utc(timestamp);
		utcDate.tz(timezone);

		this.utcDate = utcDate;
		return this.timeSignal$;
	}

	private utcDate: dayjs.Dayjs;

	private updateTime() {
		this.ngZone.run(() => {
			if (this.utcDate) this.timeSignal$.set(this.utcDate.fromNow());
		});
	}

	public static elapsedTimeFromNow(timestamp: Date | string, timezone: string = dayjs.tz.guess()): string {
		const utcDate = dayjs.utc(timestamp);
		utcDate.tz(timezone);
		return utcDate.fromNow();
	}

	ngOnDestroy() {
		this.effectRef$.destroy();
	}
}
